import { useEffect, useState } from 'react';

import { capitalizeFirstLetter } from '../../../../../../helpers/filterNames';
import IncludeAllDropdown from '../../FactsEditor/components/IncludeAllDropdown';
import { CountsChip } from '../CountsChip';
import TooltipIcon from '@/components/atoms/TooltipIcon';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

interface SubToolbarProps {
  entityType: 'facts' | 'documents' | 'chronologies' | 'inconsistencies' | 'entities' | 'inconsistency reports';
  totalCount?: number;
  totalUnfilteredCount?: number;
  totalProcessedCount?: number;
  selectedCount?: number;
  allRowsChecked: boolean;
  handleToggleAll?: (params: { checked: boolean }) => Promise<void>;
  deselectAll: () => void;
}

export const SubToolbar = ({
  entityType,
  totalCount,
  totalUnfilteredCount,
  totalProcessedCount,
  selectedCount,
  allRowsChecked,
  handleToggleAll,
  deselectAll,
}: SubToolbarProps) => {
  const [count, setCount] = useState(totalCount ?? 0);
  const [unfilteredCount, setUnfilteredCount] = useState(totalUnfilteredCount ?? 0);

  useEffect(() => {
    if (totalCount !== undefined) {
      setCount(totalCount);
    }
  }, [totalCount]);

  useEffect(() => {
    if (totalUnfilteredCount !== undefined) {
      setUnfilteredCount(totalUnfilteredCount);
    }
  }, [totalUnfilteredCount]);

  return (
    <div className="flex items-center justify-between border-b py-2 text-xs">
      <div className="ml-[1px] flex items-center space-x-2">
        {handleToggleAll && (
          <IncludeAllDropdown
            allRowsChecked={allRowsChecked}
            handleSelectAll={() => handleToggleAll({ checked: true })}
            handleClearAll={() => handleToggleAll({ checked: false })}
          />
        )}
        {selectedCount !== undefined && (
          <div className="flex items-center gap-4">
            <div className="pl-2 text-gray-600">
              <b className="text-brandSecondary">{selectedCount.toLocaleString()}</b> {entityType} selected
            </div>

            <Button size="sm" className="px-1.25 py-0.75" disabled={selectedCount === 0} onClick={deselectAll}>
              <Icon className="lucide--x" />
              <span>
                Clear All ({selectedCount}) Selected {capitalizeFirstLetter(entityType)}
              </span>
            </Button>
          </div>
        )}

        {/* Processed count for docs table */}
        {totalProcessedCount && (
          <div className="flex items-center gap-2">
            <CountsChip count={`${totalProcessedCount}/${unfilteredCount}`} label="processed" />
            {totalProcessedCount < unfilteredCount && (
              <TooltipIcon tooltipContent="Add facts manually from unprocessed documents" />
            )}
          </div>
        )}
      </div>
      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <span className="font-medium text-gray-700">Showing:</span>
          <div className="rounded px-2 py-1 text-blue-700">
            {count.toLocaleString()} of {unfilteredCount.toLocaleString()} {entityType}
          </div>
        </div>
      </div>
    </div>
  );
};
